import { apiRequest } from '@services/apiClient';
import tourPointsEndpoints from '@services/tourPoints/tourPointsEndpoints';
import { resolveMediaKind, resolveMediaUrl } from '@utils/media/mediaUrl';
import {
  buildDrivingLabelCode,
  buildDrivingRouteGroupName,
  normalizeMixedAudioByLanguage,
  normalizeRouteMusicByLanguage,
  normalizeDrivingRouteGroupKey,
} from '@utils/mode_tool/modeRoute';

function sanitizeLocalizedArrayForRequest(list = []) {
  return (Array.isArray(list) ? list : []).map((item) => {
    if (!item || typeof item !== 'object') return item;
    const entry = Object.entries(item).find(([key]) => key !== 'id');
    if (!entry) return item;
    const [iso, value] = entry;
    const normalizedValue = String(value || '');
    return {
      [iso]: normalizedValue,
      id: normalizedValue.trim() ? String(item?.id || '') : '',
    };
  });
}

function sanitizeLocalizedAiAudioDescriptionForRequest(entries = {}) {
  const source = Array.isArray(entries)
    ? entries.reduce((result, item) => {
        if (!item || typeof item !== 'object') return result;
        const entry = Object.entries(item).find(([key]) => key !== 'id');
        if (!entry) return result;
        const [iso, value] = entry;
        result[iso] = { ...(value || {}), id: String(item?.id || value?.id || '') };
        return result;
      }, {})
    : entries && typeof entries === 'object'
      ? entries
      : {};
  return Object.entries(source).reduce((result, [iso, value]) => {
    if (!iso || !value || typeof value !== 'object') return result;
    const { id, ...content } = value;
    result.push({ [iso]: content, id: String(id || '') });
    return result;
  }, []);
}

function sanitizePointForRequest(point = {}) {
  // `aiAudioDescription` is the editor's local draft key. The CMS contract
  // receives only its snake_case counterpart.
  const {
    aiAudioDescription: _aiAudioDescription,
    aiBrief: _aiBrief,
    ...pointForRequest
  } = point || {};
  const imageSource = Array.isArray(point?.images)
    ? point.images
    : (Array.isArray(point?.multimedia) ? point.multimedia : []).filter(
        (item) => resolveMediaKind(item) === 'image',
      );
  const date = new Date().toISOString().slice(0, 10);
  const images = imageSource.map((item, index) => ({
    id: String(item?.id || item?.sourceId || ''),
    url: resolveMediaUrl(String(item?.url || '')),
    updatedAt:
      String(item?.updatedAt || '').trim() ||
      `${date}T${String(index).padStart(2, '0')}:00:00.000Z`,
  }));
  return {
    ...pointForRequest,
    name: sanitizeLocalizedArrayForRequest(point?.name),
    description: sanitizeLocalizedArrayForRequest(point?.description),
    ai_audio_description: sanitizeLocalizedAiAudioDescriptionForRequest(
      point?.aiAudioDescription ?? point?.ai_audio_description,
    ),
    more_info: sanitizeLocalizedArrayForRequest(point?.more_info),
    notes: String(point?.notes ?? ''),
    ai_brief: String(point?.ai_brief ?? point?.aiBrief ?? ''),
    images,
  };
}

function sanitizeDirectionsRawPayload(raw = null) {
  if (!raw || typeof raw !== 'object') return null;
  const normalizedRoutes =
    raw?.routes && typeof raw.routes === 'object' && !Array.isArray(raw.routes)
      ? Object.entries(raw.routes).reduce((acc, [routeKey, routeValue]) => {
          const normalizedKey = String(routeKey || '').trim();
          if (!normalizedKey) return acc;
          const geometry =
            routeValue?.geometry &&
            Array.isArray(routeValue?.geometry?.coordinates)
              ? routeValue.geometry
              : null;
          if (!geometry) return acc;
          acc[normalizedKey] = {
            geometry,
            distance: Number(routeValue?.distance || 0),
            duration: Number(routeValue?.duration || 0),
          };
          return acc;
        }, {})
      : {};
  const geometry =
    raw?.geometry && Array.isArray(raw?.geometry?.coordinates)
      ? raw.geometry
      : null;
  if (!geometry && !Object.keys(normalizedRoutes).length) return null;
  return {
    profile: String(raw?.profile || ''),
    ...(Object.keys(normalizedRoutes).length ? { routes: normalizedRoutes } : {}),
  };
}

function sanitizeTourPointsPayload(payload = {}) {
  return {
    ...payload,
    directiosApiRoutePointRaw: sanitizeDirectionsRawPayload(
      payload?.directiosApiRoutePointRaw ?? null,
    ),
    points: (Array.isArray(payload?.points) ? payload.points : []).map(
      (point) => sanitizePointForRequest(point),
    ),
  };
}

function normalizeLocalizedToObject(list = []) {
  const source = Array.isArray(list) ? list : [];
  return source.reduce((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const entries = Object.entries(item).filter(([key]) => key !== 'id');
    if (!entries.length) return acc;
    const [iso, value] = entries[0];
    if (!iso) return acc;
    const normalizedValue = String(value || '');
    acc[iso] = {
      value: normalizedValue,
      id: normalizedValue.trim() ? String(item?.id || '') : '',
    };
    return acc;
  }, {});
}

function normalizeLocalizedAiAudioDescriptionToObject(list = []) {
  const source = Array.isArray(list) ? list : [];
  return source.reduce((result, item) => {
    if (!item || typeof item !== 'object') return result;
    const entry = Object.entries(item).find(([key]) => key !== 'id');
    if (!entry) return result;
    const [iso, value] = entry;
    if (!iso || !value || typeof value !== 'object') return result;
    result[iso] = { ...value, id: String(item?.id || value?.id || '') };
    return result;
  }, {});
}

function normalizeMultimedia(images = [], audios = []) {
  const date = new Date().toISOString().slice(0, 10);
  const asMedia = (item, mediaType, index = 0) => ({
    id: String(item?.id || ''),
    // Backend can return relative URLs (/public/asset/...); normalize to absolute URL.
    url: resolveMediaUrl(String(item?.url || '')),
    mediaType,
    sourceId: String(item?.id || ''),
    language: String(
      item?.language || item?.languageIso || item?.iso || '',
    ).toLowerCase(),
    ...(mediaType === 'image'
      ? {
          updatedAt:
            String(item?.updatedAt || '').trim() ||
            `${date}T${String(index).padStart(2, '0')}:00:00.000Z`,
        }
      : {
          ...(Number(item?.durationSeconds ?? item?.duration_seconds) > 0
            ? { durationSeconds: Number(item?.durationSeconds ?? item?.duration_seconds) }
            : {}),
        }),
  });
  return [
    ...(Array.isArray(images) ? images : []).map((item, index) =>
      asMedia(item, 'image', index),
    ),
    ...(Array.isArray(audios) ? audios : []).map((item) => asMedia(item, 'audio'))
  ];
}

function normalizeAllTourRoutesFromBody(body = null) {
  if (Array.isArray(body?.routes)) return body.routes;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body)) return body;
  if (body?.routes && typeof body.routes === 'object') {
    return Object.values(body.routes).filter(
      (item) => item && typeof item === 'object',
    );
  }
  if (body?.route && typeof body.route === 'object') return [body.route];
  return [];
}

function normalizeAllTourStartPointsFromBody(body = null) {
  if (Array.isArray(body?.start_points)) return body.start_points;
  if (Array.isArray(body?.startPoints)) return body.startPoints;
  if (
    body?.starting_points &&
    Array.isArray(body.starting_points)
  )
    return body.starting_points;
  return [];
}

// Converts API point shape into mode-tool internal draft shape.
export function normalizeTourPointFromApi(point = {}) {
  const pointOrder = Number(point?.number_point || 0) || 1;
  const fallbackPointId = `point-${pointOrder}`;
  const pointId = String(point?.id || fallbackPointId);
  const routeGroup = normalizeDrivingRouteGroupKey(
    point?.route_group ?? point?.routeGroup ?? '',
    point?.route_group_name ?? point?.routeGroupName ?? '',
    pointOrder,
  );
  const routeGroupName = buildDrivingRouteGroupName(pointOrder, routeGroup);
  return {
    id: pointId,
    order: pointOrder,
    latitude: Number(point?.latitude ?? 0) || 0,
    longitude: Number(point?.longitude ?? 0) || 0,
    name: normalizeLocalizedToObject(point?.name),
    description: normalizeLocalizedToObject(point?.description),
    aiAudioDescription: normalizeLocalizedAiAudioDescriptionToObject(
      point?.ai_audio_description ?? point?.aiAudioDescription,
    ),
    more_info: normalizeLocalizedToObject(point?.more_info),
    notes: String(point?.notes ?? point?.note ?? ''),
    ai_brief: String(point?.ai_brief ?? point?.aiBrief ?? ''),
    music: normalizeRouteMusicByLanguage(point?.music).map((item) => ({
      ...item,
      url: resolveMediaUrl(String(item.url || '')),
    })),
    mixed_audio: normalizeMixedAudioByLanguage(point?.mixed_audio, point?.audios)
      .map((item) => ({
        ...item,
        url: resolveMediaUrl(String(item?.url || '')),
      })),
    radiusMeters: Number(point?.radius_meters ?? 0) || 0,
    turnCase: Number(point?.turn_case ?? 0) || 0,
    pointType: String(point?.pointType ?? point?.point_type ?? ''),
    routeGroup,
    routeGroupName,
    labelCode: buildDrivingLabelCode(point, pointOrder - 1),
    routeGroupParentPointId: '',
    multimedia: normalizeMultimedia(point?.images, point?.audios),
    isPersisted: true
  };
}

export function createDrivingTourPointsRequest(payload) {
  return apiRequest(tourPointsEndpoints.drivingBase, {
    method: 'POST',
    body: sanitizeTourPointsPayload(payload)
  });
}

export function updateDrivingTourPointsRequest(payload) {
  return apiRequest(tourPointsEndpoints.drivingBase, {
    method: 'PUT',
    body: sanitizeTourPointsPayload(payload)
  });
}

export function createWalkingTourPointsRequest(payload) {
  return apiRequest(tourPointsEndpoints.walkingBase, {
    method: 'POST',
    body: sanitizeTourPointsPayload(payload)
  });
}

export function updateWalkingTourPointsRequest(payload) {
  return apiRequest(tourPointsEndpoints.walkingBase, {
    method: 'PUT',
    body: sanitizeTourPointsPayload(payload)
  });
}

export function createRandomTourPointsRequest(payload) {
  return apiRequest(tourPointsEndpoints.randomBase, {
    method: 'POST',
    body: sanitizeTourPointsPayload(payload)
  });
}

export function updateRandomTourPointsRequest(payload) {
  return apiRequest(tourPointsEndpoints.randomBase, {
    method: 'PUT',
    body: sanitizeTourPointsPayload(payload)
  });
}

export function listWalkingRoutesByTourRequest(tourId) {
  return apiRequest(tourPointsEndpoints.walkingByTour(encodeURIComponent(String(tourId || ''))));
}

export function listRandomRouteByTourRequest(tourId) {
  return apiRequest(tourPointsEndpoints.randomByTour(encodeURIComponent(String(tourId || ''))));
}

export function listAllTourPointsByTourRequest(tourId) {
  return apiRequest(
    tourPointsEndpoints.allByTour(encodeURIComponent(String(tourId || ''))),
  ).then((response) => ({
    ...response,
    body: {
      ...(response?.body && typeof response.body === 'object' ? response.body : {}),
      routes: normalizeAllTourRoutesFromBody(response?.body),
      start_points: normalizeAllTourStartPointsFromBody(response?.body),
    },
  }));
}

export function deleteWalkingRouteRequest(routeTypeId) {
  return apiRequest(tourPointsEndpoints.walkingByRouteType(encodeURIComponent(String(routeTypeId || ''))), {
    method: 'DELETE'
  });
}

export function listTourPointsByTourTypeRequest(tourTypeId, modeType = '', routeKey = '') {
  const encodedTourTypeId = encodeURIComponent(tourTypeId || '');
  const normalizedModeType = String(modeType || '').trim().toLowerCase();
  if (normalizedModeType === 'walking') {
    const targetId = encodeURIComponent(String(routeKey || tourTypeId || ''));
    return apiRequest(tourPointsEndpoints.walkingByRouteType(targetId));
  }
  if (normalizedModeType === 'random') {
    return apiRequest(tourPointsEndpoints.randomByTour(encodedTourTypeId));
  }
  return apiRequest(tourPointsEndpoints.drivingByTourType(encodedTourTypeId));
}

export function deleteTourPointRequest(tourTypeId, pointNumber) {
  return apiRequest(
    tourPointsEndpoints.drivingByTourTypePoint(
      encodeURIComponent(tourTypeId || ''),
      encodeURIComponent(String(pointNumber || ''))
    ),
    { method: 'DELETE' }
  );
}
